<template>
  <v-layout
    class="mt-3"
    align-start
    justify-center
  >
    <v-flex xs10>
      <RichTextEditor
        ref='richTextEditor'
        v-bubble.snackbar.error
        v-bind="$props"
        :tinymceKey="modeOptions.commenterKey"
        :interaction.sync="localInteraction"
        :text-model.sync="localTextModel"
        :inline="true"
        :initial-text="initialText"
        :bulb-id="localInteraction.bulbId"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        :root-teacher="rootTeacher"
        mode='comment'
      ></RichTextEditor>
    </v-flex>
    <v-flex
      xs2
      class="text-xs-center"
    >
      <v-btn
        v-if="(
          mode !== 'comment'
          || localTextModel.length
        )"
        :disabled="localIsProcessingData"
        color="primary"
        flat
        icon
        @click="modeOptions.submit()"
        :aria-label="modeOptions.submissionButtonAriaLabel"
      >
        <v-icon :size="24">
          {{modeOptions.icon}}
        </v-icon>
      </v-btn>
      <v-tooltip
        v-if="(
          mode === 'comment'
          && localTextModel.length === 0
        )"
        top
      >
        <v-btn
          slot="activator"
          @click="$emit('refresh-discussion-comments')"
          :disabled="localIsProcessingData"
          icon
          flat
          color="primary"
          aria-label="Refresh discussion forum."
          class="d-inline-flex ml-0"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
        <span>Refresh the discussion form.</span>
      </v-tooltip>
    </v-flex>
  </v-layout>
</template>

<script>
import api from '../../../services/api';
import Interaction from '../../../mixins/Interaction';
import RichTextEditor from '../../Snippets/RichTextEditor.vue';

export default {
  name: 'DiscussionInput',
  components: {
    RichTextEditor,
  },
  mixins: [
    Interaction,
  ],
  props: {
    initialText: String,
    ariaLabel: String,
    placeholder: String,
    visible: Boolean,
    textModel: {
      type: String,
      required: true,
    },
    comment: {
      type: Object,
      required: false,
    },
    mode: {
      type: String,
      validator(mode) {
        return [
          'comment',
          'edit',
          'reply',
        ].includes(mode);
      },
    },
  },
  computed: {
    localTextModel: {
      get() {
        return this.textModel || '';
      },
      set(textModel) {
        this.$emit('update:textModel', textModel);
      },
    },
    modeOptions() {
      if (this.mode === 'comment') {
        return {
          commenterKey: `discussion-comment-${this.localInteraction.id}-new`,
          submissionButtonAriaLabel: 'Create a new comment.',
          submit: this.discussionRole === 'anonymous' ? this.createClientsideDiscussionComment : this.createDiscussionComment,
          icon: 'send',
        };
      } else if (this.mode === 'edit') {
        return {
          commenterKey: `discussion-edit-${this.comment.id || 0}`,
          submissionButtonAriaLabel: 'Update an existing comment.',
          submit: this.discussionRole === 'anonymous' ? this.updateClientsideDiscussionComment : this.updateDiscussionComment,
          icon: 'edit',
        };
      } else if (this.mode === 'reply') {
        return {
          commenterKey: `discussion-reply-${this.comment.id || 0}`,
          submissionButtonAriaLabel: 'Reply to another comment.',
          submit: () => {
            if (this.discussionRole === 'anonymous') {
              this.createClientsideDiscussionComment(this.comment.id);
            } else {
              this.createDiscussionComment(this.comment.id);
            }
          },
          icon: 'reply',
        };
      }
      return {
        commenterKey: `commenter-rogue-${Date.now()}`,
        submissionButtonAriaLabel: 'Discussion forum submission button.',
        submit: () => {},
        icon: 'send',
      };
    },
  },
  methods: {
    updateRichTextEditorText(text) {
      if (this.$refs.richTextEditor) this.$refs.richTextEditor.updateTinymceText(text);
    },
    createClientsideDiscussionComment(parentId) {
      this.$emit('populate-comments', [
        {
          id: this.$generateNumber(`discussion-${this.localInteraction.id}-comments`, true),
          teacherId: this.teacher.id,
          interactionId: this.localInteraction.id,
          text: this.localTextModel,
          displayTime: this.displayTime,
          self: true,
          deleted: false,
          clientsideOnly: true,
          parentId,
          student: {
            id: 1,
            firstName: 'Anonymous',
            lastName: 'User',
          },
          replies: [],
          created: new Date(),
        },
      ]);
      this.updateRichTextEditorText('');
      this.$emit('toggle-replies', true);
      this.$emit('toggle-reply', false);
    },
    async createDiscussionComment(parentId) {
      try {
        this.localIsProcessingData = true;
        const { data: newComment } = await api.createDiscussionComment({
          teacherId: this.teacher.id,
          interactionId: this.localInteraction.id,
          interactionAttemptId: this.localInteractionAttempt.id,
          sectionId: this.localBulbAttempt.sectionId,
          text: this.localTextModel,
          displayTime: this.displayTime,
          parentId,
        });
        this.updateRichTextEditorText('');
        this.$emit('populate-comments', [newComment]);
        if (parentId) this.$emit('toggle-replies', true);
        this.$emit('toggle-reply', false);
      } catch(e) {
        this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
    updateClientsideDiscussionComment() {
      this.$emit('populate-comments', [
        {
          ...this.comment,
          text: this.localTextModel,
          updated: new Date(),
        },
      ]);
      this.updateRichTextEditorText('');
      this.$emit('toggle-edit', false);
    },
    async updateDiscussionComment() {
      try {
        this.localIsProcessingData = true;
        const { data: updatedComment } = await api.updateDiscussionComment({
          id: this.comment.id,
          text: this.localTextModel,
        });
        this.updateRichTextEditorText('');
        this.$emit('populate-comments', [updatedComment]);
        this.$emit('toggle-edit', false);
      } catch(e) {
        this.$emit('error', e);
      } finally {
        this.localIsProcessingData = false;
      }
    },
  },
  async mounted() {
    await this.$nextTick();
    this.updateRichTextEditorText(this.initialText);
  },
};
</script>